import { useEffect, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"

import { useImageModal } from "@fuse/sharedComponents/globalImageModal"
import { showErrorToast } from "@fuse/sharedComponents/themeToast/toastUtils"
import {
  CouponApplyOrRemoveStatus,
  EVENT_CLASS,
  TAnalyticsEventParams,
  TCouponDetail,
  TTicketForAttendee,
} from "@letshang/constants"
import {
  castArray,
  filter,
  get,
  groupBy,
  isEmpty,
  keyBy,
  map,
  mapValues,
  sumBy,
} from "lodash"
import { useRecoilState } from "recoil"
import { useSessionStorage } from "usehooks-ts"

import { userState } from "@fuse/recoil/signup"

import {
  ELogAnalyticEvent,
  logAnalyticEvent,
} from "@firebaseConfig/analyticsUtil"

import { applyOrRemoveCoupon } from "@api/firebaseWrappedApis"
import {
  useCreatePaymentSheet,
  useGetAllTickets,
} from "@api/services/events/useEventsApi"

import useFetchEventDetails from "@fuse/pages/eventPage/useFetchEventDetails"
import { EBuyTicketSessionKeys } from "@fuse/pages/eventSignUp/BuyTickets/utils"
import { groupTicketsWithPrice } from "@fuse/pages/eventSignUp/eventHelper"

import MouthNone from "@fuse/assets/gif/gif-mouth-none.gif"

interface PaymentPayload {
  eventId: string
  questionnaires: any
  tickets: TTicketForAttendee[]
  donation?: {
    customAmount?: number
    ticketId?: string
  }
  coupon?: {
    codeApplied: string
    discountAmount: number
  }
}

export const useStripeCheckout = (invitedId?: string, pagename?: string) => {
  const params = useParams()
  const eventId = get(params, "eventId", "")
  const location = useLocation()

  const [isLoading, setIsLoading] = useState(false)
  const [user, setUser] = useRecoilState(userState)
  const loggedInUserId = get(user, "uid", "")

  const { showImageAlert, hideImageAlert } = useImageModal()

  const { state, refresh: refreshEventData } = useFetchEventDetails(eventId)

  const eventLoading = get(state, "isLoading", false)
  const currentEventDetails = get(state, "eventDetails", {})
  const eventDocData = get(currentEventDetails, "eventDocData", {})
  const flyerImage = get(currentEventDetails, "eventDocData.flyerImage", "")

  const eventClass = get(
    currentEventDetails,
    "eventDocData.eventClass",
    EVENT_CLASS.NON_TICKETED_EVENT,
  ) as EVENT_CLASS
  const isTicketedEvent = eventClass === EVENT_CLASS.TICKETED_EVENT

  const { data: tickets, isLoading: allTicketLoader } =
    useGetAllTickets(eventId)
  const donations = get(tickets, "data.donations", [])

  const [selectedDonateAmount, setSelectedDonateAmount] =
    useSessionStorage<number>(EBuyTicketSessionKeys.SELECTED_DONATE_AMOUNT, 0)

  const isAdmin = get(eventDocData, "initiatorUid", null) === loggedInUserId

  const onLogAnalyticEvent = (): void | undefined => {
    const groupedTickets = groupBy(ticketsToBuy, "ticketId")
    const mapTicketDetails = groupTicketsWithPrice(groupedTickets)
    const finalPrice = sumBy(mapTicketDetails, "finalPrice")

    const commonParams: TAnalyticsEventParams = {
      uid: loggedInUserId,
      page_name: pagename,
      event_id: eventId,
      event_name: get(eventDocData, "eventName", ""),
      event_type: isTicketedEvent
        ? EVENT_CLASS.TICKETED_EVENT
        : EVENT_CLASS.NON_TICKETED_EVENT,
      amount: finalPrice,
    }

    logAnalyticEvent(ELogAnalyticEvent.STRIPE_CHECKOUT_WEB, commonParams)
  }

  useEffect(() => {
    if (!isEmpty(eventDocData) && !isAdmin) {
      onLogAnalyticEvent()
    }
  }, [eventDocData])

  const getTheErrorMsgStatement = (error: string) => {
    if (error.includes("following capabilities enabled")) {
      return {
        description:
          "The host needs to enable ticket purchases.\n Check back shortly!",
        okButtonTitle: "Got it",
      }
    }

    return {
      title: "Payment failed!",
      description: `${error} \n\n If you need help, contact our support team at <hl>letshangsupport@gmail.com</hl>`,
      okButtonTitle: "Update payment details",
    }
  }

  const getTheErrorToastFields = (error: string) => {
    const { description, okButtonTitle, ...rest } =
      getTheErrorMsgStatement(error)
    return {
      description,
      okButtonTitle,
      ...rest,
    }
  }

  const paymentFailedToast = (error: string) => {
    showImageAlert({
      image: MouthNone,
      ...getTheErrorToastFields(error),
      okButtonPress: () => {
        hideImageAlert()
        console.log("OK button pressed")
      },
    })
  }

  const {
    mutate,
    data: paymentSheetData,
    isLoading: paymentSheetLoading,
  } = useCreatePaymentSheet()

  const ticketsToBuy = castArray(get(location, "state.ticketsToBuy", []))
  const ticketsForAttendee: TTicketForAttendee[] = map(
    ticketsToBuy,
    function (obj) {
      const addOns = map(
        filter(get(obj, "ticketAddOns", []), ["isSelected", true]),
        function (obj) {
          return {
            ticketId: get(obj, "ticketId", ""),
          }
        },
      )

      return {
        ticketId: get(obj, "id", get(obj, "ticketId", "")),
        attendeeId: get(obj, "attendeeId", ""),
        amount: get(obj, "ticketPrice", 0),
        addOns: addOns,
        ...(!!invitedId && { invitedId: invitedId }),
      }
    },
  )

  const onInitPayment = (
    codeApplied: string = "",
    discountAmount: number = undefined,
  ) => {
    return new Promise((resolve, reject) => {
      const ticketsAnswersToQuestions = mapValues(
        keyBy(ticketsToBuy, "attendeeId"),
        "attendeeQuestionersAnswer",
      )

      const payload: PaymentPayload = {
        eventId: eventId,
        questionnaires: ticketsAnswersToQuestions,
        tickets: ticketsForAttendee,
      }

      // Add coupon object only if both values are present
      if (codeApplied && discountAmount) {
        payload.coupon = {
          codeApplied,
          discountAmount,
        }
      }

      if (selectedDonateAmount > 0) {
        payload.donation = {
          customAmount: selectedDonateAmount,
        }
      }

      mutate(payload, {
        onSuccess: (data) => {
          resolve(data)
        },
        onError: (err: any) => {
          // Handle error - you can perform any actions needed upon error here
          const errMsg = err.message.includes("following capabilities enabled")
            ? "The host needs to enable ticket purchases. Check back shortly!"
            : err.message
          showErrorToast(`Error: ${errMsg}`)
          reject(err)
        },
      })
    })
  }

  const applyCoupon = async (details: TCouponDetail) => {
    const result = await applyOrRemoveCoupon({
      ...details,
      status: CouponApplyOrRemoveStatus.APPLY,
    })
    return result
  }

  const removeCoupon = async (details: TCouponDetail) => {
    const result = await applyOrRemoveCoupon({
      ...details,
      status: CouponApplyOrRemoveStatus.REMOVE,
    })
    return result
  }

  return {
    eventId,
    isLoading:
      isLoading || eventLoading || allTicketLoader || paymentSheetLoading,
    onInitPayment,
    setIsLoading,
    ticketsToBuy,
    refreshEventData,
    user,
    flyerImage,
    paymentFailedToast,
    eventDetail: eventDocData,
    currentEventDetails,
    applyCoupon,
    removeCoupon,
    donations,
    selectedDonateAmount,
  }
}
